import React from "react";
import {
  createBulkFormTagAction,
  type CreateBulkFormTagActionData,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { throwIfFormServiceError } from "src/services/error";
import { useBulkActionTargets } from "../forms/components/bulkActionTargetsContext";
import {
  trackFormAction,
  trackFormActionError,
} from "../forms/components/trackFormAction";
import { useBulkActionProgressToast } from "../forms/components/useBulkActionProgressToast";
import { useRequireBulkTargets } from "../forms/components/useRequireBulkTargets";
import type { BulkTargets } from "../forms/types";

export type BulkFormTagActionType = NonNullable<
  CreateBulkFormTagActionData["body"]
>["actionType"];

type EnqueueBulkFormTagArgs = {
  actionType: BulkFormTagActionType;
  tagId: uuid;
};

function createBulkFormTagBody(
  actionType: BulkFormTagActionType,
  targets: BulkTargets,
  tagId: uuid
): CreateBulkFormTagActionData["body"] {
  switch (actionType) {
    case "ADD_TAG":
      return {
        ...targets,
        actionType: "ADD_TAG",
        actionData: { tag_id: tagId },
      };
    case "REMOVE_TAG":
      return {
        ...targets,
        actionType: "REMOVE_TAG",
        actionData: { tag_id: tagId },
      };
  }
}

export function useEnqueueBulkFormTag() {
  const toast = useAvelaToast();
  const showProgressToast = useBulkActionProgressToast();
  const { clearSelection, selectionSize } = useBulkActionTargets();
  const requireTargets = useRequireBulkTargets();

  return React.useCallback(
    async ({ actionType, tagId }: EnqueueBulkFormTagArgs): Promise<boolean> => {
      try {
        const targets = await requireTargets();
        if (!targets) {
          return false;
        }

        const response = throwIfFormServiceError(
          await createBulkFormTagAction({
            body: createBulkFormTagBody(actionType, targets, tagId),
          })
        );

        const trackedActionType =
          actionType === "ADD_TAG" ? "ADD_TAG" : "REMOVE_TAG";
        trackFormAction({
          verb: "Queued",
          actionType: trackedActionType,
          bulk: true,
          count: selectionSize,
          async: true,
          extra: { tag_id: tagId },
        });

        showProgressToast({
          bulkActionId: response.data.bulkActionId,
          formTemplateId: targets.formTemplateId,
          actionType: trackedActionType,
          selectionSize,
        });
        clearSelection();
        return true;
      } catch (err) {
        trackFormActionError(err, {
          actionType: actionType === "ADD_TAG" ? "ADD_TAG" : "REMOVE_TAG",
          bulk: true,
          count: selectionSize,
          async: true,
          extra: { tag_id: tagId },
        });
        toast.error({
          title: "Oops! Something went wrong!",
          description: "Check your network and try again.",
        });
        console.error(err);
        return false;
      }
    },
    [clearSelection, requireTargets, selectionSize, showProgressToast, toast]
  );
}
